import { Injectable, Pipe, PipeTransform, inject } from '@angular/core'
import { ConfirmationService } from '@openng/optimus-ui/api'

export type FqConfirmAction =
  | 'delete' | 'danger'
  | 'edit' | 'warning'
  | 'create' | 'success'
  | 'info' | 'primary'

export interface FqConfirmOptions {
  /** Titolo della dialog (header). */
  header: string
  /** Testo del messaggio. */
  message?: string
  /**
   * Determina la tinta dell'header e del bottone di conferma.
   * Accetta sia il nome dell'azione sia la severity:
   * - `delete` / `danger`  -> rosso (default focus su Annulla)
   * - `edit` / `warning`   -> ambra
   * - `create` / `success` -> verde
   * - `info` / `primary`   -> blu navy (default)
   */
  action?: FqConfirmAction
  acceptLabel?: string
  rejectLabel?: string
  /** Focus iniziale: sul bottone di conferma (default) o su Annulla. */
  defaultFocus?: 'accept' | 'reject'
}

/** Chiavi "tono" condivise tra ConfirmDialog e p-dialog. */
export type FqConfirmTone = 'delete' | 'edit' | 'create' | 'info'

type FqConfirmToneConfig = {
  key: FqConfirmTone
  severity: 'danger' | 'warn' | 'success' | 'primary'
}

const TONES: Record<FqConfirmAction, FqConfirmToneConfig> = {
  delete: { key: 'delete', severity: 'danger' },
  danger: { key: 'delete', severity: 'danger' },
  edit: { key: 'edit', severity: 'warn' },
  warning: { key: 'edit', severity: 'warn' },
  create: { key: 'create', severity: 'success' },
  success: { key: 'create', severity: 'success' },
  info: { key: 'info', severity: 'primary' },
  primary: { key: 'info', severity: 'primary' }
}

/** Normalizza una action/severity nella chiave tono condivisa (default `info`). */
export function fqConfirmTone(action?: FqConfirmAction): FqConfirmTone {
  return TONES[action ?? 'info'].key
}

/**
 * Restituisce la classe da passare allo `styleClass` di un `<p-dialog>`
 * per sfumare l'header con la stessa tinta del ConfirmDialog, es.
 * `<p-dialog [styleClass]="fqDialogToneClass('edit')">`.
 */
export function fqDialogToneClass(action?: FqConfirmAction): string {
  return `fq-dialog-${fqConfirmTone(action)}`
}

/**
 * Pipe standalone che converte una action/severity nella classe `fq-dialog-*`,
 * per lo `styleClass` di un `<p-dialog>`:
 * `[styleClass]="(isNew ? 'create' : 'edit') | fqDialogTone"`.
 */
@Pipe({ name: 'fqDialogTone', standalone: true })
export class FqDialogTonePipe implements PipeTransform {
  transform(action?: FqConfirmAction): string {
    return fqDialogToneClass(action)
  }
}

/**
 * Wrapper di `ConfirmationService` (Optimus) che sostituisce SweetAlert2
 * per le conferme. Usa un unico `<p-confirmdialog>` montato nel root,
 * espone una Promise<boolean> e colora header/bottone in base all'azione.
 */
@Injectable({ providedIn: 'root' })
export class FqConfirmService {
  private readonly confirmation = inject(ConfirmationService)

  confirm(options: FqConfirmOptions): Promise<boolean> {
    const tone = TONES[options.action ?? 'info']

    return new Promise<boolean>((resolve) => {
      this.confirmation.confirm({
        header: options.header,
        message: options.message,
        acceptLabel: options.acceptLabel ?? 'Confirm',
        rejectLabel: options.rejectLabel ?? 'Cancel',
        defaultFocus: options.defaultFocus ?? (tone.key === 'delete' ? 'reject' : 'accept'),
        acceptButtonStyleClass: `fq-confirm-accept-${tone.key}`,
        rejectButtonStyleClass: 'fq-confirm-reject',
        acceptButtonProps: { severity: tone.severity },
        rejectButtonProps: { severity: 'secondary' },
        accept: () => resolve(true),
        reject: () => resolve(false)
      } as any)
    })
  }
}
